import { useEffect, useMemo, useRef, useState } from "react";
import { TurnFrame, type GameProps } from "../pages/Match";
import { banner, pop } from "../components/juice";
import { buzz, play } from "../sound";
import { Piece } from "./ChessPieces";

const canAct = (v: any) => v.state.phase === "action" && v.state.turn === v.state.you;

/**
 * Runs `fn(now, before)` after the render in which `sig` changed – but not for the first one (opening a match that is already under way
 * must stay silent). `sig` should be a small string/number describing "what is on the board".
 */
function useBoardChange<S>(sig: S, fn: (now: S, before: S) => void) {
  const before = useRef<S | undefined>(undefined);
  const first = useRef(true);
  const cb = useRef(fn);
  cb.current = fn;
  useEffect(() => {
    if (first.current) { first.current = false; before.current = sig; return; }
    const b = before.current as S;
    before.current = sig;
    if (!Object.is(b, sig)) cb.current(sig, b);
  }, [sig]);
}

/* ───────────── Kryds og bolle ───────────── */
function XO({ kind }: { kind: 0 | 1 }) {
  return kind === 0
    ? <svg className="xo x" viewBox="0 0 100 100" aria-hidden="true"><path pathLength={100} d="M26 26 L74 74" /><path pathLength={100} d="M74 26 L26 74" /></svg>
    : <svg className="xo o" viewBox="0 0 100 100" aria-hidden="true"><circle pathLength={100} cx="50" cy="50" r="25" /></svg>;
}
const cellCentre = (i: number) => [(((i % 3) + 0.5) / 3) * 100, ((Math.floor(i / 3) + 0.5) / 3) * 100] as const;

export function Kryds(p: GameProps) {
  const st = p.view.state;
  const b = st.board;
  const you: 0 | 1 = st.you;
  const act = canAct(p.view) && !p.busy;
  const sig = b.cells.map((c: number | null) => (c === null ? "." : c)).join("");
  const [fresh, setFresh] = useState(-1);
  useBoardChange(sig, (now: string, before: string) => {
    const i = [...now].findIndex((ch, k) => ch !== "." && before[k] === ".");
    if (i < 0) return;
    setFresh(i);
    play(Number(now[i]) === you ? "place" : "place2");
    buzz(12);
  });
  const opp = p.view.players.find((x: any) => x.seat !== p.view.you);
  const line: number[] | undefined = b.line ?? undefined;
  const [x1, y1] = line ? cellCentre(line[0]!) : [0, 0];
  const [x2, y2] = line ? cellCentre(line[line.length - 1]!) : [0, 0];
  return (
    <TurnFrame {...p}>
      <div className="board-frame"><div className="kryds" role="grid" aria-label="Kryds og bolle">
        {b.cells.map((c: number | null, i: number) => (
          <button
            key={i}
            role="gridcell"
            disabled={!act || c !== null}
            className={`${c === 0 ? "x" : c === 1 ? "o" : ""} ${b.line?.includes(i) ? "win" : ""} ${fresh === i ? "fresh" : ""}`}
            aria-label={`Felt ${Math.floor(i / 3) + 1}, ${(i % 3) + 1}: ${c === null ? "tomt" : c === 0 ? "kryds" : "bolle"}`}
            onClick={() => p.act({ type: "mark", cell: i })}
          >
            {c !== null ? <span className="mark"><XO kind={c as 0 | 1} /></span> : <span className="ghostmark"><XO kind={you} /></span>}
          </button>
        ))}
        {line && (
          <svg className="winline" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            <line x1={x1} y1={y1} x2={x2} y2={y2} pathLength={100} />
          </svg>
        )}
      </div></div>
      <div className="kx-legend">
        <span className={`kx-chip ${you === 0 ? "me" : ""}`}><i><XO kind={0} /></i>{you === 0 ? "Dig" : opp?.username ?? "Modstander"}</span>
        <span className={`kx-chip ${you === 1 ? "me" : ""}`}><i><XO kind={1} /></i>{you === 1 ? "Dig" : opp?.username ?? "Modstander"}</span>
      </div>
      <small className="muted center">Du er {you === 0 ? "kryds (✕)" : "bolle (◯)"}.</small>
    </TurnFrame>
  );
}

/* ───────────── Fire på stribe ───────────── */
export function Fire(p: GameProps) {
  const st = p.view.state;
  const b = st.board;
  const act = canAct(p.view) && !p.busy;
  const cols = b.grid[0].length;
  const win = new Set((b.line ?? []).map(([r, c]: number[]) => `${r},${c}`));
  const you: 0 | 1 = st.you;
  const sig = b.grid.map((row: (number | null)[]) => row.map((c) => (c === null ? "." : c)).join("")).join("/");
  const [last, setLast] = useState("");
  useBoardChange(sig, (now: string, before: string) => {
    const rows = now.split("/"), old = before.split("/");
    for (let r = 0; r < rows.length; r++) for (let c = 0; c < rows[r]!.length; c++) {
      if (rows[r]![c] !== "." && old[r]![c] === ".") { setLast(`${r},${c}`); play("drop"); buzz(14); }
    }
  });
  return (
    <TurnFrame {...p}>
      <div className="board-frame has-head"><div className="c4" role="group" aria-label="Fire på stribe – vælg en kolonne">
        {Array.from({ length: cols }, (_, c) => (
          <button key={c} className="col" disabled={!act || b.grid[0][c] !== null} aria-label={`Kolonne ${c + 1}`} onClick={() => p.act({ type: "drop", col: c })}>
            <i className={`hint ${you === 0 ? "p0" : "p1"}`} aria-hidden="true" />
            {b.grid.map((row: (number | null)[], r: number) => (
              <span key={r} className={`hole ${win.has(`${r},${c}`) ? "win" : ""} ${last === `${r},${c}` ? "last" : ""}`}>{row[c] !== null && <i className={`disc ${row[c] === 0 ? "p0" : "p1"}`} style={{ ["--r" as any]: r + 1 }} />}</span>
            ))}
          </button>
        ))}
      </div></div>
      <small className="muted center">Du er {you === 0 ? "blå" : "orange"}. Tryk på en kolonne for at lade en brik falde.</small>
    </TurnFrame>
  );
}

/* ───────────── Kortkamp ───────────── */
const COLOR_NAME: Record<string, string> = { r: "rød", y: "gul", g: "grøn", b: "blå", w: "joker" };
const VAL: Record<string, string> = { skip: "⛔", rev: "⇄", d2: "+2", wild: "★", w4: "+4" };
function CardFace({ c, onClick, disabled, small, style, cls }: { c: any; onClick?: () => void; disabled?: boolean; small?: boolean; style?: any; cls?: string }) {
  const label = `${COLOR_NAME[c.color]} ${c.value === "skip" ? "spring over" : c.value === "rev" ? "vend" : c.value === "d2" ? "træk to" : c.value === "wild" ? "skift farve" : c.value === "w4" ? "skift farve og træk fire" : c.value}`;
  const v = VAL[c.value] ?? c.value;
  return (
    <button className={`pcard ${c.color} ${small ? "small" : ""} ${cls ?? ""}`} style={style} disabled={disabled} onClick={onClick} aria-label={label.trim()}>
      <span className="corner tl">{v}</span>
      <span className="oval"><span className="big">{v}</span></span>
      <span className="corner br">{v}</span>
    </button>
  );
}

/** width of an element, kept up to date (so the fan of cards can squeeze together instead of being cut off) */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((e) => setW(e[0]?.contentRect.width ?? el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

export function Kortkamp(p: GameProps) {
  const b = p.view.state.board;
  const mineAct = canAct(p.view) && !p.busy && !p.view.state.question;
  const [wild, setWild] = useState<number | null>(null);
  const [edu, setEdu] = useState(false);
  const you = p.view.state.you;
  const nameOf = (i: number) => p.view.players.find((x: any) => x.seat === i)?.username ?? `Spiller ${i + 1}`;
  const opp = b.handCounts.map((n: number, i: number) => ({ n, i })).filter((x: any) => x.i !== you);
  const isWild = (c: any) => c.value === "wild" || c.value === "w4";
  const play_ = (c: any) => {
    if (isWild(c)) return setWild(c.id);
    const body: any = { type: "play", card: c.id };
    if (b.eduNeeded) body.edu = edu;
    setEdu(false);
    return p.act(body);
  };
  const n = b.hand.length;
  const attacked = b.attack && b.attack.seat === you;
  const [handRef, handW] = useWidth<HTMLDivElement>();
  const cardW = 74;
  const spacing = n > 1 ? Math.max(24, Math.min(cardW - 8, (handW - cardW - 24) / (n - 1))) : cardW;
  const overlap = handW ? Math.round(spacing - cardW) : -10;

  /* what just happened on the table: who played the top card, which cards are new in my hand, did someone reach their last card */
  const [origin, setOrigin] = useState<"me" | "opp" | "">("");
  const [fresh, setFresh] = useState<Set<number>>(new Set());
  const seen = useRef<{ top: number; hand: number[]; opp: number[] } | null>(null);
  useEffect(() => {
    const now = { top: b.top.id as number, hand: b.hand.map((c: any) => c.id as number), opp: opp.map((o: any) => o.n as number) };
    const before = seen.current;
    seen.current = now;
    if (!before) return;
    if (now.top !== before.top) {
      setOrigin(now.hand.length < before.hand.length ? "me" : "opp");
      play("play");
    }
    const added = now.hand.filter((id: number) => !before.hand.includes(id));
    if (added.length) { setFresh(new Set(added)); play("deal"); }
    opp.forEach((o: any, k: number) => {
      if (o.n === 1 && (before.opp[k] ?? 9) > 1) { banner("EDU! – én kort tilbage", "pink"); play("edu"); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.top.id, b.hand.map((c: any) => c.id).join(","), opp.map((o: any) => o.n).join(",")]);

  return (
    <TurnFrame {...p}>
      <div className="notice" role="status" style={{ textAlign: "center" }}>
        Retning: <strong>{b.dir === 1 ? "med uret ↻" : "mod uret ↺"}</strong>
        {attacked ? <> · <strong>Du er angrebet med +{b.attack.n}!</strong> Svar rigtigt for at slippe for kortene.</> : b.attack ? <> · {nameOf(b.attack.seat)} er angrebet med +{b.attack.n}</> : null}
      </div>
      <div className="cardtable" data-c={b.color}>
        {opp.map((o: any) => (
          <div key={o.i} className={`oppbox ${b.turn === o.i ? "turn" : ""}`}>
            <div className="oppfan" aria-label={`${nameOf(o.i)} har ${o.n} kort`}>
              {Array.from({ length: Math.min(o.n, 12) }, (_, k) => <span key={k} className="pcard back mini" style={{ ["--k" as any]: k - Math.min(o.n, 12) / 2 }} />)}
            </div>
            <small className="muted">{nameOf(o.i)}{b.turn === o.i ? " (tur)" : ""}: <strong>{o.n}</strong> kort{o.n === 1 ? " – EDU!" : ""}</small>
          </div>
        ))}
        <div className="piles">
          <span className={`dirring ${b.dir === 1 ? "cw" : "ccw"}`} aria-hidden="true" />
          <div className="center">
            <div className="pcard back drawpile" aria-hidden="true"><span className="brain">★</span></div>
            <small className="muted">Bunke: {b.drawCount}</small>
          </div>
          <div className="center">
            <CardFace key={b.top.id} c={b.top} cls={`topcard ${origin ? `from-${origin}` : ""}`} disabled />
            <small className="muted">Aktiv farve: <strong>{COLOR_NAME[b.color]}</strong></small>
          </div>
        </div>
      </div>
      {mineAct && (
        <div className="row" style={{ justifyContent: "center" }}>
          {b.canDraw && <button className="btn cta" onClick={() => p.act({ type: "draw" })}>Du kan ikke lægge – træk et kort</button>}
          {b.drawn !== null && <button className="btn ghost" onClick={() => p.act({ type: "pass" })}>Behold kortet og slut turen</button>}
          {!b.canDraw && b.drawn === null && b.legal.length > 0 && <small className="muted">Din tur: tryk på et lysende kort med samme farve eller tal.</small>}
          {b.eduNeeded && <button type="button" className={`btn edubtn ${edu ? "lime on" : "cta"}`} aria-pressed={edu} onClick={() => setEdu(!edu)}>{edu ? "✓ Jeg siger EDU!" : "Sig EDU! (så har du kun ét kort tilbage)"}</button>}
        </div>
      )}
      <div className="hand fan" role="group" aria-label="Din hånd" ref={handRef} style={{ ["--ov" as any]: `${overlap}px` }}>
        {b.hand.map((c: any, i: number) => (
          <CardFace key={c.id} c={c} cls={`${mineAct && b.legal.includes(c.id) ? "legal" : ""} ${fresh.has(c.id) ? "fresh" : ""}`} style={{ ["--i" as any]: i - (n - 1) / 2, ["--n" as any]: n }} disabled={!mineAct || !b.legal.includes(c.id)} onClick={() => play_(c)} />
        ))}
      </div>
      {wild !== null && (
        <div className="notice" role="dialog" aria-label="Vælg farve">
          Vælg farve til jokeren:
          <div className="row">
            {(["r", "y", "g", "b"] as const).map((col) => (
              <button key={col} className={`pcard small ${col}`} style={{ cursor: "pointer" }} aria-label={COLOR_NAME[col]} onClick={() => { const id = wild; setWild(null); const body: any = { type: "play", card: id, color: col }; if (b.eduNeeded) body.edu = edu; setEdu(false); void p.act(body); }}>
                <span className="oval"><span className="big">{COLOR_NAME[col]![0]!.toUpperCase()}</span></span>
              </button>
            ))}
            <button className="btn ghost small" onClick={() => setWild(null)}>Annuller</button>
          </div>
        </div>
      )}
    </TurnFrame>
  );
}

/* ───────────── Sænke slagskibe ───────────── */
interface Ship { len: number; x: number; y: number; horizontal: boolean; hits?: number }
const cellsOf = (s: Ship) => Array.from({ length: s.len }, (_, i) => ({ x: s.x + (s.horizontal ? i : 0), y: s.y + (s.horizontal ? 0 : i) }));

function Axis({ n }: { n: number }) {
  return <div className="seaaxis" style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }} aria-hidden="true">{Array.from({ length: n }, (_, i) => <span key={i} className="lab">{String.fromCharCode(65 + i)}</span>)}</div>;
}

/** a ship drawn over the water grid: hull with a pointed bow, deck stripe and cabins */
function Hull({ s, tone }: { s: Ship; tone: "mine" | "sunk" | "ghost" | "bad" | "fleet" }) {
  const w = s.horizontal ? s.len : 1, h = s.horizontal ? 1 : s.len;
  return (
    <div className={`hull ${s.horizontal ? "h" : "v"} ${tone} len${s.len}`} style={{ gridColumn: `${s.x + 1} / span ${w}`, gridRow: `${s.y + 1} / span ${h}` }}>
      <i className="deck" /><i className="cab c1" /><i className="cab c2" />
    </div>
  );
}
function Hulls({ n, children }: { n: number; children?: any }) {
  return <div className="hulls" style={{ gridTemplateColumns: `repeat(${n}, 1fr)`, gridTemplateRows: `repeat(${n}, 1fr)` }} aria-hidden="true">{children}</div>;
}
/** row numbers 1…n down the left side */
function Rows({ n }: { n: number }) {
  return <div className="searows" aria-hidden="true" style={{ gridTemplateRows: `repeat(${n}, 1fr)` }}>{Array.from({ length: n }, (_, i) => <span key={i}>{i + 1}</span>)}</div>;
}

export function Slagskibe(p: GameProps) {
  const st = p.view.state;
  const b = st.board;
  const n: number = b.size;
  const [ships, setShips] = useState<Ship[]>([]);
  const [horizontal, setHorizontal] = useState(true);
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  const next = b.fleetLens[ships.length];
  const occupied = useMemo(() => new Set(ships.flatMap((s) => cellsOf(s).map((c) => `${c.x},${c.y}`))), [ships]);
  const candidate = (x: number, y: number): { ship: Ship; ok: boolean } | null => {
    if (next === undefined) return null;
    const ship = { len: next, x, y, horizontal };
    const cs = cellsOf(ship);
    const ok = cs.every((c) => c.x < n && c.y < n && !occupied.has(`${c.x},${c.y}`));
    return { ship, ok };
  };
  const cand = hover ? candidate(hover.x, hover.y) : null;

  /* R or Space turns the next ship while placing the fleet */
  useEffect(() => {
    if (b.phase !== "setup") return;
    const h = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.matches?.("input,textarea,select")) return;
      if (e.key === "r" || e.key === "R") { e.preventDefault(); setHorizontal((v) => !v); }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [b.phase]);

  /* shots: a flash and a sound for every new shot, from you or from the opponent; a banner when a ship goes down */
  const shotSig = b.phase === "setup" ? "setup" : `${b.outgoing.map((r: number[]) => r.join("")).join("/")}|${b.incoming.map((r: number[]) => r.join("")).join("/")}`;
  const sunkCount = (b.sunk?.length ?? 0) as number;
  const lostCount = (b.myShips ?? []).filter((s: Ship) => (s.hits ?? 0) >= s.len).length as number;
  const [lastOut, setLastOut] = useState("");
  const [lastIn, setLastIn] = useState("");
  useBoardChange(shotSig, (now: string, before: string) => {
    if (now === "setup" || before === "setup") return;
    const [no, ni] = now.split("|"), [bo, bi] = before.split("|");
    const diff = (a: string, o: string) => {
      const A = a.split("/"), O = o.split("/");
      for (let y = 0; y < A.length; y++) for (let x = 0; x < A[y]!.length; x++) if (A[y]![x] !== O[y]![x]) return { x, y, v: A[y]![x]! };
      return null;
    };
    const mine = diff(no!, bo!), theirs = diff(ni!, bi!);
    const hit = mine ?? theirs;
    if (mine) setLastOut(`${mine.x},${mine.y}`);
    if (theirs) setLastIn(`${theirs.x},${theirs.y}`);
    if (!hit) return;
    play("cannon");
    setTimeout(() => {
      if (hit.v === "2") { play("hit"); buzz([60, 30, 60]); pop(mine ? "Træffer!" : "Du blev ramt!", mine ? "gold" : "pink"); }
      else { play("miss"); pop("Forbi!", "soft"); }
    }, 260);
  });
  useBoardChange(sunkCount, (now: number, before: number) => { if (now > before) { setTimeout(() => { play("sink"); banner("Skib sænket!", "gold"); buzz([80, 40, 120]); }, 520); } });
  useBoardChange(lostCount, (now: number, before: number) => { if (now > before) setTimeout(() => banner("Et af dine skibe er sænket", "soft"), 520); });

  if (b.phase === "setup") {
    if (b.placed[b.you]) {
      return (
        <TurnFrame {...p}>
          <div className="notice" role="status">Din flåde er klar. Venter på modstanderen…</div>
        </TurnFrame>
      );
    }
    return (
      <TurnFrame {...p} wide>
        <div className="setupgrid">
          <div className="seaframe setup">
            <div className="sea" style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }} role="grid" aria-label="Din havn" onMouseLeave={() => setHover(null)}>
              {Array.from({ length: n * n }, (_, i) => {
                const x = i % n, y = Math.floor(i / n);
                const k = `${x},${y}`;
                return (
                  <button key={i} className={occupied.has(k) ? "ship" : ""} aria-label={`Felt ${String.fromCharCode(65 + x)}${y + 1}${occupied.has(k) ? ", skib" : ""}`}
                    onMouseEnter={() => setHover({ x, y })} onFocus={() => setHover({ x, y })}
                    onContextMenu={(e: any) => { e.preventDefault(); setHorizontal((h) => !h); }}
                    onClick={() => { const c = candidate(x, y); if (c?.ok) { setShips((s) => [...s, c.ship]); play("place"); } else play("lock"); }} />
                );
              })}
            </div>
            <Hulls n={n}>
              {ships.map((s, i) => <Hull key={i} s={s} tone="mine" />)}
              {cand && <Hull s={cand.ship} tone={cand.ok ? "ghost" : "bad"} />}
            </Hulls>
          </div>
          <div className="stack setupside">
            <p>Placér din flåde ({b.fleetLens.join(", ")} felter lange). {next !== undefined ? <>Næste skib: <strong>{next} felter</strong>.</> : "Alle skibe er placeret."}</p>
            <div className="fleettray" aria-hidden="true">
              {b.fleetLens.map((len: number, i: number) => (
                <span key={i} className={`fl-ship ${i < ships.length ? "done" : i === ships.length ? "now" : ""}`} style={{ ["--len" as any]: len }}><i /></span>
              ))}
            </div>
            <div className="row">
              <button className="btn ghost small" onClick={() => setHorizontal((h) => !h)}>Retning: {horizontal ? "vandret ↔" : "lodret ↕"}</button>
              <button className="btn ghost small" disabled={!ships.length} onClick={() => setShips((s) => s.slice(0, -1))}>Fortryd</button>
              <button className="btn ghost small" onClick={() => p.act({ type: "auto" })} disabled={p.busy}>Placér tilfældigt</button>
            </div>
            <button className="btn lime" disabled={ships.length !== b.fleetLens.length || p.busy} onClick={() => p.act({ type: "place", ships })}>Bekræft flåde</button>
            <small className="muted">Tip: tryk på R eller højreklik for at dreje skibet.</small>
          </div>
        </div>
      </TurnFrame>
    );
  }

  const mine = canAct(p.view) && !p.busy;
  const sunkCells = new Set((b.sunk as Ship[]).concat(b.revealed ?? []).flatMap((s: Ship) => cellsOf(s).map((c) => `${c.x},${c.y}`)));
  const fireHere = (x: number, y: number) => p.act({ type: "fire", x, y });
  return (
    <TurnFrame {...p} wide statusExtra={<span className="muted"> · Tilbage hos fjenden: {b.remaining.join(", ") || "intet"}</span>}>
      <div className="sea-wrap">
        <div className="seabox mine">
          <div className="panel-title">Din havn</div>
          <Axis n={n} />
          <div className="seabody"><Rows n={n} />
            <div className="seaframe">
              <div className="sea" style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }} aria-label="Din havn">
                {Array.from({ length: n * n }, (_, i) => {
                  const x = i % n, y = Math.floor(i / n);
                  const inc = b.incoming[y][x];
                  return <div key={i} className={`${inc === 2 ? "hit" : inc === 1 ? "miss" : ""} ${lastIn === `${x},${y}` ? "lastshot" : ""}`}>{inc === 2 ? <span className="boom" /> : inc === 1 ? <span className="splash" /> : null}</div>;
                })}
              </div>
              <Hulls n={n}>{(b.myShips ?? []).map((s: Ship, i: number) => <Hull key={i} s={s} tone={(s.hits ?? 0) >= s.len ? "sunk" : "mine"} />)}</Hulls>
            </div>
          </div>
        </div>
        <div className={`seabox foe ${mine ? "aim" : ""}`}>
          <div className="panel-title">Fjendens farvand {mine ? "– tryk for at skyde" : ""}</div>
          <Axis n={n} />
          <div className="seabody"><Rows n={n} />
            <div className="seaframe">
              <div className="sea" style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }} role="grid" aria-label="Fjendens farvand">
                {Array.from({ length: n * n }, (_, i) => {
                  const x = i % n, y = Math.floor(i / n);
                  const v = b.outgoing[y][x];
                  const name = `${String.fromCharCode(65 + x)}${y + 1}`;
                  const cls = sunkCells.has(`${x},${y}`) && v === 2 ? "sunk" : v === 2 ? "hit" : v === 1 ? "miss" : b.revealed && sunkCells.has(`${x},${y}`) ? "ship" : "";
                  return (
                    <button key={i} className={`${cls} ${lastOut === `${x},${y}` ? "lastshot" : ""}`} disabled={!mine || v !== 0} aria-label={`${name}: ${v === 2 ? "træffer" : v === 1 ? "forbi" : "uskudt"}`} onClick={() => fireHere(x, y)}>
                      {v === 2 ? <span className="boom" /> : v === 1 ? <span className="splash" /> : null}
                    </button>
                  );
                })}
              </div>
              <Hulls n={n}>{(b.sunk as Ship[]).map((s, i) => <Hull key={i} s={s} tone="sunk" />)}</Hulls>
            </div>
          </div>
        </div>
      </div>
    </TurnFrame>
  );
}

/* ───────────── Skak ───────────── */
const PIECE_NAME: Record<string, string> = { k: "konge", q: "dame", r: "tårn", b: "løber", n: "springer", p: "bonde" };
const sq = (i: number) => String.fromCharCode(97 + (i % 8)) + (8 - Math.floor(i / 8));
const START_COUNT: Record<string, number> = { p: 8, n: 2, b: 2, r: 2, q: 1 };
const WORTH: Record<string, number> = { p: 1, n: 3, b: 3, r: 5, q: 9 };

/** the pieces each side has lost, and who is ahead in material */
function lost(board: string[]) {
  const have: Record<string, number> = {};
  for (const c of board) if (c) have[c] = (have[c] ?? 0) + 1;
  const out = { w: [] as string[], b: [] as string[], diff: 0 };
  for (const t of "qrbnp") {
    for (let k = (have[t.toUpperCase()] ?? 0); k < START_COUNT[t]!; k++) { out.w.push(t); out.diff -= WORTH[t]!; }
    for (let k = (have[t] ?? 0); k < START_COUNT[t]!; k++) { out.b.push(t); out.diff += WORTH[t]!; }
  }
  return out; // diff > 0 = white is ahead
}

export function Skak(p: GameProps) {
  const st = p.view.state;
  const b = st.board;
  const mine = canAct(p.view) && !p.busy;
  const flip = b.you === 1;
  const [sel, setSel] = useState<string | null>(null);
  const [promo, setPromo] = useState<{ from: string; to: string } | null>(null);
  const targets = useMemo(() => new Set(sel ? b.legal.filter((u: string) => u.startsWith(sel)).map((u: string) => u.slice(2, 4)) : []), [sel, b.legal]);
  const froms = new Set(b.legal.map((u: string) => u.slice(0, 2)));
  const order = Array.from({ length: 64 }, (_, k) => (flip ? 63 - k : k));
  const pieces = b.board.filter(Boolean).length as number;
  const cap = useMemo(() => lost(b.board), [b.board]);

  /* sound for every move (a soft knock; a thud for a capture; two notes for check) */
  useBoardChange(`${b.lastMove ?? ""}|${pieces}`, (now: string, before: string) => {
    const [mv, cnt] = now.split("|"), [, cb] = before.split("|");
    if (!mv) return;
    const captured = Number(cnt) < Number(cb);
    play(captured ? "capture" : "move");
    buzz(captured ? 30 : 10);
    if (b.check && !b.winner) setTimeout(() => { play("check"); banner("Skak!", "gold"); }, 220);
  });

  const posOf = (sqName: string) => { const i = (8 - Number(sqName[1])) * 8 + (sqName.charCodeAt(0) - 97); const d = flip ? 63 - i : i; return [d % 8, Math.floor(d / 8)] as const; };
  const slide = b.lastMove ? (() => { const [fx, fy] = posOf(b.lastMove.slice(0, 2)), [tx, ty] = posOf(b.lastMove.slice(2, 4)); return { to: b.lastMove.slice(2, 4), dx: fx - tx, dy: fy - ty }; })() : null;
  const kingSq = b.check && !b.winner ? b.board.findIndex((c: string) => c === (st.turn === 0 ? "K" : "k")) : -1;

  const click = (i: number) => {
    if (!mine) return;
    const name = sq(i);
    if (sel && targets.has(name)) {
      const isPromo = b.legal.some((u: string) => u.startsWith(sel + name) && u.length === 5);
      if (isPromo) setPromo({ from: sel, to: name });
      else { setSel(null); void p.act({ type: "move", from: sel, to: name }); }
      return;
    }
    const pick = froms.has(name) && name !== sel;
    setSel(pick ? name : null);
    if (pick) play("click");
  };
  const tray = (list: string[], white: boolean) => (
    <span className="captray" aria-label={white ? "Slåede hvide brikker" : "Slåede sorte brikker"}>
      {list.map((t, k) => <i key={k}><Piece type={t} white={white} /></i>)}
    </span>
  );
  const ahead = flip ? -cap.diff : cap.diff;
  return (
    <TurnFrame {...p} wide statusExtra={b.check && !b.winner ? <span> · <strong>Skak!</strong></span> : null}>
      <div className="chess-wrap">
        <div className="capbar">{tray(flip ? cap.w : cap.b, flip)}{ahead < 0 && <b className="adv">+{-ahead}</b>}</div>
        <div className="board-wood"><div className="chess" role="grid" aria-label="Skakbræt">
          {order.map((i, k) => {
            const piece: string = b.board[i];
            const name = sq(i);
            const dark = (Math.floor(i / 8) + (i % 8)) % 2 === 1;
            const white = piece && piece === piece.toUpperCase();
            const isTo = slide?.to === name && piece;
            return (
              <button key={i} className={`${dark ? "dk" : ""} ${sel === name ? "sel" : ""} ${b.lastMove && (b.lastMove.startsWith(name) || b.lastMove.slice(2, 4) === name) ? "last" : ""} ${targets.has(name) && piece ? "cap" : ""} ${kingSq === i ? "chk" : ""}`}
                style={{ ["--l" as any]: "#f0d9b5", ["--d" as any]: "#b58863" }}
                aria-label={`${name}${piece ? `, ${white ? "hvid" : "sort"} ${PIECE_NAME[piece.toLowerCase()]}` : ""}${targets.has(name) ? ", mulig" : ""}`}
                onClick={() => click(i)}>
                {k % 8 === 0 && <i className="rk" aria-hidden="true">{8 - Math.floor(i / 8)}</i>}
                {k >= 56 && <i className="fl" aria-hidden="true">{String.fromCharCode(97 + (i % 8))}</i>}
                {piece && <span key={`${i}${piece}`} className={`${white ? "w" : "b"} piece ${isTo ? "slide" : ""}`} style={isTo ? { ["--dx" as any]: slide!.dx, ["--dy" as any]: slide!.dy } : undefined}><Piece type={piece} white={!!white} /></span>}
                {targets.has(name) && !piece && <span className="dot" />}
              </button>
            );
          })}
        </div></div>
        <div className="capbar">{tray(flip ? cap.b : cap.w, !flip)}{ahead > 0 && <b className="adv">+{ahead}</b>}</div>
      </div>
      <small className="muted center">Du spiller {flip ? "sort" : "hvid"}. Vælg en brik, og vælg derefter et felt. {b.reason ? `Resultat: ${b.reason}` : ""}</small>
      {promo && (
        <div className="notice" role="dialog" aria-label="Forvandl bonde">
          Forvandl bonden til:
          <div className="row">
            {(["q", "r", "b", "n"] as const).map((k) => (
              <button key={k} className="btn promo" onClick={() => { const m = promo; setPromo(null); setSel(null); void p.act({ type: "move", from: m.from, to: m.to, promo: k }); }}><span className="pi"><Piece type={k} white={!flip} /></span>{PIECE_NAME[k]}</button>
            ))}
          </div>
        </div>
      )}
    </TurnFrame>
  );
}
